import { useCallback, useEffect, useRef, useState } from 'react';
import {
  collection,
  query,
  orderBy,
  addDoc,
  serverTimestamp,
  getDocs,
  startAfter,
  limit,
  QueryDocumentSnapshot,
  DocumentData,
  getCountFromServer,
  QueryConstraint,
  where,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../lib/firebase';
import {
  inferAssetType,
  inferMimeTypeFromName,
  isNormalizedAssetType,
} from '../lib/assetNormalization';

export interface Asset {
  id: string;
  name: string;
  type: 'image' | 'video' | 'audio';
  storageUrl: string;
  thumbnailUrl?: string;
  previewUrl?: string;
  mimeType: string;
  size: number;
  width?: number;
  height?: number;
  source: 'manual' | 'mcp';
  createdAt: Date;
  market?: string;
}

export type AssetTypeFilter = 'all' | Asset['type'];

const ASSETS_PAGE_SIZE = 24;

function getString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

function getNestedString(
  value: unknown,
  key: string,
): string | undefined {
  if (!value || typeof value !== 'object') return undefined;
  return getString((value as Record<string, unknown>)[key]);
}

function getNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function toDate(value: unknown): Date | null {
  if (value instanceof Date) return value;
  if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
    return value.toDate() as Date;
  }
  return null;
}

function mapAssetDoc(doc: QueryDocumentSnapshot<DocumentData>): Asset {
  const data = doc.data();
  const mimeType = getString(data.mimeType)
    ?? getNestedString(data.metadata, 'mimeType')
    ?? inferMimeTypeFromName(getString(data.name) ?? getString(data.filename) ?? getString(data.storageUrl))
    ?? '';
  return {
    id: doc.id,
    name: getString(data.name) ?? getString(data.filename) ?? 'Untitled asset',
    type: inferAssetType({
      type: getString(data.type),
      mimeType,
      name: getString(data.name),
      filename: getString(data.filename),
      storageUrl: getString(data.storageUrl),
      previewUrl: getString(data.previewUrl),
      thumbnailUrl: getString(data.thumbnailUrl),
    }),
    storageUrl: getString(data.storageUrl) ?? '',
    thumbnailUrl: getString(data.thumbnailUrl),
    previewUrl: getString(data.previewUrl),
    mimeType,
    size: getNumber(data.size) ?? 0,
    width: getNumber(data.width),
    height: getNumber(data.height),
    source: (getString(data.source) as Asset['source'] | undefined) ?? 'manual',
    market: data.market,
    createdAt: toDate(data.createdAt) ?? toDate(data.uploadedAt) ?? new Date(),
  };
}

function buildAssetsPageQueryConstraints(
  assetTypeFilter: AssetTypeFilter,
  cursor: QueryDocumentSnapshot<DocumentData> | null,
): QueryConstraint[] {
  const constraints: QueryConstraint[] = [];
  if (assetTypeFilter !== 'all') {
    constraints.push(where('type', '==', assetTypeFilter));
  }
  constraints.push(orderBy('createdAt', 'desc'));
  if (cursor) {
    constraints.push(startAfter(cursor));
  }
  constraints.push(limit(ASSETS_PAGE_SIZE));
  return constraints;
}

async function fetchAssetsPage(
  assetTypeFilter: AssetTypeFilter,
  cursor: QueryDocumentSnapshot<DocumentData> | null,
): Promise<{
  assets: Asset[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}> {
  const snapshot = await getDocs(
    query(collection(db, 'assets'), ...buildAssetsPageQueryConstraints(assetTypeFilter, cursor))
  );

  return {
    assets: snapshot.docs.map(mapAssetDoc),
    lastDoc: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : cursor,
    hasMore: snapshot.docs.length === ASSETS_PAGE_SIZE,
  };
}

export function useAssets(assetTypeFilter: AssetTypeFilter = 'all') {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const requestIdRef = useRef(0);

  const fetchInitialAssets = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setLoadingMore(false);
    setError(null);
    setAssets([]);
    setLastDoc(null);
    setHasMore(false);
    try {
      const page = await fetchAssetsPage(assetTypeFilter, null);
      if (requestId !== requestIdRef.current) return;
      setAssets(page.assets);
      setLastDoc(page.lastDoc);
      setHasMore(page.hasMore);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setAssets([]);
      setLastDoc(null);
      setHasMore(false);
      setError(err instanceof Error ? err.message : 'Failed to load assets');
    } finally {
      if (requestId !== requestIdRef.current) return;
      setLoading(false);
    }
  }, [assetTypeFilter]);

  useEffect(() => {
    fetchInitialAssets();
  }, [fetchInitialAssets]);

  useEffect(() => {
    const fetchCount = async () => {
      const requestId = requestIdRef.current;
      try {
        const constraints: QueryConstraint[] = [];
        if (assetTypeFilter !== 'all') {
          constraints.push(where('type', '==', assetTypeFilter));
        }
        const countSnapshot = await getCountFromServer(
          query(collection(db, 'assets'), ...constraints),
        );
        if (requestId !== requestIdRef.current) return;
        setTotalCount(countSnapshot.data().count);
      } catch {
        if (requestId !== requestIdRef.current) return;
        setTotalCount(null);
      }
    };
    setTotalCount(null);
    fetchCount();
  }, [assetTypeFilter]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore || !lastDoc) return;
    const requestId = requestIdRef.current;
    setLoadingMore(true);
    setError(null);
    try {
      const page = await fetchAssetsPage(assetTypeFilter, lastDoc);
      if (requestId !== requestIdRef.current) return;
      setAssets((prev) => {
        const existingIds = new Set(prev.map((asset) => asset.id));
        const deduped = page.assets.filter((asset) => !existingIds.has(asset.id));
        return [...prev, ...deduped];
      });
      setLastDoc(page.lastDoc);
      setHasMore(page.hasMore);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load more assets');
    } finally {
      if (requestId !== requestIdRef.current) return;
      setLoadingMore(false);
    }
  }, [assetTypeFilter, hasMore, lastDoc, loadingMore]);

  const refreshAssets = useCallback(async () => {
    await fetchInitialAssets();
    try {
      const constraints: QueryConstraint[] = [];
      if (assetTypeFilter !== 'all') {
        constraints.push(where('type', '==', assetTypeFilter));
      }
      const countSnapshot = await getCountFromServer(query(collection(db, 'assets'), ...constraints));
      setTotalCount(countSnapshot.data().count);
    } catch {
      setTotalCount(null);
    }
  }, [assetTypeFilter, fetchInitialAssets]);

  const uploadAsset = async (file: File, source: 'manual' | 'mcp' = 'manual'): Promise<string> => {
    try {
      console.log(`[useAssets] upload start — file="${file.name}" size=${file.size} type="${file.type}"`);

      const resolvedMimeType = file.type || inferMimeTypeFromName(file.name) || '';
      const resolvedType = inferAssetType({
        mimeType: resolvedMimeType,
        name: file.name,
      });
      const isImage = resolvedType === 'image';
      const isVideo = resolvedType === 'video';
      const isAudio = resolvedType === 'audio';

      if (!isImage && !isVideo && !isAudio) {
        throw new Error('File must be an image, video, or audio file');
      }

      const storedType = isNormalizedAssetType(resolvedType) ? resolvedType : 'image';
      const timestamp = Date.now();
      const storagePath = `assets/gallery/${storedType}s/${timestamp}_${file.name}`;

      // Upload to Firebase Storage
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      console.log(`[useAssets] bytes uploaded — file="${file.name}"`);

      console.log(`[useAssets] fetching download URL — file="${file.name}"`);
      const downloadURL = await getDownloadURL(storageRef);
      console.log(`[useAssets] download URL obtained — file="${file.name}" url="${downloadURL}"`);

      // Create Firestore record
      const assetDoc = await addDoc(collection(db, 'assets'), {
        name: file.name,
        type: storedType,
        storageUrl: downloadURL,
        mimeType: resolvedMimeType,
        size: file.size,
        source,
        createdAt: serverTimestamp(),
      });

      console.log(`[useAssets] upload success — file="${file.name}" id="${assetDoc.id}"`);
      await refreshAssets();
      return assetDoc.id;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      console.error(`[useAssets] upload failure — file="${file.name}" error="${errorMessage}"`);
      throw new Error(errorMessage);
    }
  };

  return {
    assets,
    loading,
    loadingMore,
    error,
    hasMore,
    totalCount,
    uploadAsset,
    loadMore,
  };
}
